/**
 * Citadel API client — minimal fetch wrapper for /v1/citadel/* operon endpoints.
 *
 * Intentionally re-implemented from scratch (no dependency on platform-ui)
 * to preserve the operational-independence boundary. Citadel must build,
 * test, and deploy without any cross-app coupling.
 *
 * Sprint: SPR-26.O | BL-29-1790 | BL-29-2837
 */

const OPERON_URL: string =
    import.meta.env.VITE_OPERON_URL ??
    'https://operon-staging.up.railway.app';

let cachedAccessToken: string | null = null;
let cachedTenantId: string | null = null;

/** Set the bearer token used by every subsequent apiFetch call. */
export function setAccessToken(token: string | null): void {
    cachedAccessToken = token;
}

/**
 * Set the tenant id sent as the `X-Tenant-ID` header on every subsequent
 * apiFetch call.
 *
 * BL-29-2163: operon's `InjectTenantContext` middleware needs an explicit
 * `X-Tenant-ID` header. Its JWT-claim fallback reads the BARE `tenant_id`
 * claim, but Auth0 stamps the NAMESPACED `https://rodiun.io/tenant_id`
 * claim — so without this header operon resolves an empty tenant context
 * and 400s. citadel-ui must port the tenant-header layer platform-ui
 * already carries; see `auth.ts` for the claim extraction.
 */
export function setTenantId(id: string | null): void {
    cachedTenantId = id;
}

/**
 * Returns true once BOTH the bearer token AND the tenant id have been
 * resolved from the Auth0 session. Callers gate their initial data load
 * on this so the first request does not race the async token+tenant
 * resolution (BL-29-2163 — the 401 race).
 */
export function isApiReady(): boolean {
    return cachedAccessToken !== null && cachedTenantId !== null;
}

// ── Error envelope (BL-29-2837) ────────────────────────────────────────
//
// Operon emits the canonical envelope defined in
// workspace/operon/internal/errors/api_error.go (BL-29-427 + BL-29-2818):
//
//   {
//     "error": {
//       "code":        "MACHINE_READABLE_CODE",
//       "message":     "Human-readable description",
//       "details":     <any>,           // optional
//       "trace_id":    "req-...",       // optional but typically present
//       "timestamp":   "...Z",          // RFC3339Nano UTC, optional
//       "suggestion":  "...",           // optional
//       "allowed":     ["live", ...]    // optional (validation errors)
//     }
//   }
//
// trace_id + timestamp pair is the Fail-Loud invariant (CLAUDE.md
// Fail-Loud Doctrine, PD-ratified 2026-05-21): every error envelope is
// independently correlatable to a log line and a wall-clock instant.
// Silently flattening that signal in the client breaks the invariant.

/**
 * Machine-readable code surfaced when the response body cannot be parsed
 * as the canonical operon envelope. The wire body is preserved in
 * `bodyText` (and truncated into `message`) for diagnostic display.
 *
 * Per the Fail-Loud Doctrine: a parser that silently coerces unknown
 * shapes to "Unknown error" obscures upstream regressions. Surfacing
 * MALFORMED_ERROR_ENVELOPE with the raw body lets the admin see what
 * operon actually emitted and lets us alert on it.
 */
export const MALFORMED_ERROR_ENVELOPE = 'MALFORMED_ERROR_ENVELOPE';

/**
 * Surfaced when apiFetch is invoked before Auth0 has resolved both a
 * bearer token AND a tenant id (BL-29-2163 401 race backstop).
 */
export const ERR_API_NOT_READY = 'ERR_API_NOT_READY';

/**
 * Maximum number of characters of the raw body to inline into the
 * CitadelApiError.message when the envelope is malformed. The full
 * body is always preserved on `bodyText` regardless.
 */
const MAX_RAW_BODY_IN_MESSAGE = 500;

/**
 * The canonical operon error envelope shape on the wire. Mirrors
 * `errors.APIError` in `workspace/operon/internal/errors/api_error.go`.
 * Field names are snake_case to match the JSON tags exactly.
 */
export interface OperonApiErrorBody {
    code: string;
    message: string;
    details?: unknown;
    trace_id?: string;
    timestamp?: string;
    suggestion?: string;
    allowed?: string[];
}

/** Top-level wrapper: `{ error: OperonApiErrorBody }`. */
export interface OperonApiErrorEnvelope {
    error: OperonApiErrorBody;
}

/** Constructor parameters for {@link CitadelApiError}. */
export interface CitadelApiErrorInit {
    status: number;
    code: string | null;
    message: string;
    bodyText: string;
    traceId?: string | null;
    timestamp?: string | null;
    details?: unknown;
    suggestion?: string | null;
    allowed?: readonly string[] | null;
}

/**
 * Typed error thrown by {@link apiFetch} for every non-2xx response, plus
 * the ERR_API_NOT_READY backstop and MALFORMED_ERROR_ENVELOPE parser
 * failure modes.
 *
 * Every canonical envelope field is surfaced as a public readonly field:
 *
 * - `status`     HTTP status code (0 for the ERR_API_NOT_READY backstop).
 * - `code`       Machine-readable code from `error.code`, or
 *                `MALFORMED_ERROR_ENVELOPE` / `ERR_API_NOT_READY`.
 * - `message`    Human-readable description.
 * - `bodyText`   Raw response body (always preserved, even on malformed
 *                envelopes — diagnostic value).
 * - `traceId`    `error.trace_id` — pass to logs / Sentry breadcrumbs /
 *                the admin error UI.
 * - `timestamp`  `error.timestamp` — RFC3339Nano UTC instant the envelope
 *                was constructed on the server.
 * - `details`    `error.details` — structured detail payload.
 * - `suggestion` `error.suggestion` — actionable hint for the admin.
 * - `allowed`    `error.allowed` — permitted discrete values on
 *                validation errors.
 */
export class CitadelApiError extends Error {
    public readonly status: number;
    public readonly code: string | null;
    public readonly bodyText: string;
    public readonly traceId: string | null;
    public readonly timestamp: string | null;
    public readonly details: unknown;
    public readonly suggestion: string | null;
    public readonly allowed: readonly string[] | null;

    constructor(init: CitadelApiErrorInit) {
        super(init.message);
        this.name = 'CitadelApiError';
        this.status = init.status;
        this.code = init.code;
        this.bodyText = init.bodyText;
        this.traceId = init.traceId ?? null;
        this.timestamp = init.timestamp ?? null;
        this.details = init.details ?? null;
        this.suggestion = init.suggestion ?? null;
        this.allowed = init.allowed ?? null;
    }
}

/** Truncate `text` to {@link MAX_RAW_BODY_IN_MESSAGE} with an ellipsis. */
function truncateForMessage(text: string): string {
    if (text.length <= MAX_RAW_BODY_IN_MESSAGE) return text;
    return `${text.slice(0, MAX_RAW_BODY_IN_MESSAGE)}...`;
}

/**
 * Narrow an unknown value to {@link OperonApiErrorEnvelope}. A canonical
 * envelope must carry `error.code` (non-empty string) AND `error.message`
 * (non-empty string); every other field is optional. Same minimum-field
 * contract enforced by `error_envelope_contract_test.go` on the server.
 */
function isCanonicalEnvelope(value: unknown): value is OperonApiErrorEnvelope {
    if (typeof value !== 'object' || value === null) return false;
    const v = value as { error?: unknown };
    if (typeof v.error !== 'object' || v.error === null) return false;
    const e = v.error as { code?: unknown; message?: unknown };
    return (
        typeof e.code === 'string' &&
        e.code.length > 0 &&
        typeof e.message === 'string' &&
        e.message.length > 0
    );
}

/**
 * Parse an HTTP error response into a typed CitadelApiError.
 *
 * Three terminal branches, all fail-loud:
 *
 * 1. Non-JSON body → MALFORMED_ERROR_ENVELOPE, raw text in message.
 * 2. JSON but not the canonical shape → MALFORMED_ERROR_ENVELOPE, raw
 *    text in message.
 * 3. Canonical envelope → surface every field (code, message, trace_id,
 *    timestamp, details, suggestion, allowed) on the resulting error.
 *
 * No fallback to a flat `{code, message}` shape — operon is the
 * canonical authority and the flat shape is dead per CLAUDE.md
 * §No backward compatibility hacks.
 */
function parseErrorResponse(status: number, bodyText: string): CitadelApiError {
    let parsed: unknown;
    try {
        parsed = JSON.parse(bodyText);
    } catch {
        return new CitadelApiError({
            status,
            code: MALFORMED_ERROR_ENVELOPE,
            message: `Non-JSON error response from operon (HTTP ${status}): ${truncateForMessage(
                bodyText,
            )}`,
            bodyText,
        });
    }

    if (!isCanonicalEnvelope(parsed)) {
        return new CitadelApiError({
            status,
            code: MALFORMED_ERROR_ENVELOPE,
            message: `Malformed error envelope from operon (HTTP ${status}): ${truncateForMessage(
                bodyText,
            )}`,
            bodyText,
        });
    }

    const body = parsed.error;
    return new CitadelApiError({
        status,
        code: body.code,
        message: body.message,
        bodyText,
        traceId: body.trace_id ?? null,
        timestamp: body.timestamp ?? null,
        details: body.details ?? null,
        suggestion: body.suggestion ?? null,
        allowed: body.allowed ?? null,
    });
}

interface ApiFetchOpts {
    method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
    body?: string;
    headers?: Record<string, string>;
    signal?: AbortSignal;
}

export async function apiFetch<T>(
    path: string,
    opts: ApiFetchOpts = {},
): Promise<T> {
    // BL-29-2163 backstop: never fire a tokenless / tenantless request.
    // operon's JWTAuth 401s a missing bearer token and InjectTenantContext
    // 400s a missing tenant context. Callers SHOULD gate on isApiReady()
    // (see FeatureSwitches / TemplatesModeration) — this throw is the
    // defence-in-depth layer that turns any early call into a typed,
    // recoverable error rather than a confusing server-side 401/400.
    if (!isApiReady()) {
        throw new CitadelApiError({
            status: 0,
            code: ERR_API_NOT_READY,
            message:
                'Citadel API client is not ready — Auth0 token + tenant id not yet resolved.',
            bodyText: '',
        });
    }
    const url = `${OPERON_URL.replace(/\/$/, '')}${path}`;
    const headers: Record<string, string> = {
        Accept: 'application/json',
        ...(opts.body ? { 'Content-Type': 'application/json' } : {}),
        ...(cachedAccessToken
            ? { Authorization: `Bearer ${cachedAccessToken}` }
            : {}),
        // BL-29-2163: operon's InjectTenantContext middleware needs the
        // explicit X-Tenant-ID header (its JWT-claim fallback reads the
        // bare `tenant_id` claim that Auth0 never stamps). X-Workspace-ID
        // defaults to 'personal'. X-Deployment-Target is intentionally
        // omitted — operon defaults an absent value to 'live', whereas a
        // bad value 400s.
        ...(cachedTenantId
            ? {
                  'X-Tenant-ID': cachedTenantId,
                  'X-Workspace-ID': 'personal',
              }
            : {}),
        ...(opts.headers ?? {}),
    };
    const res = await fetch(url, {
        method: opts.method ?? 'GET',
        body: opts.body,
        headers,
        signal: opts.signal,
        credentials: 'omit',
    });

    const text = await res.text();
    if (!res.ok) {
        throw parseErrorResponse(res.status, text);
    }

    if (!text) return undefined as T;
    return JSON.parse(text) as T;
}

// ── Feature flags (BL-29-1790) ─────────────────────────────────────────

export interface PlatformFeatureFlag {
    key: string;
    value: boolean;
    updated_at: string;
    updated_by_sub: string;
    description: string | null;
}

export interface ListPlatformFeatureFlagsResponse {
    flags: PlatformFeatureFlag[];
}

/**
 * GET /v1/citadel/feature-flags
 *
 * Returns every override row. Empty array when no flags are toggled
 * (Citadel falls back to compile-time defaults in that case — see
 * BUILD_TIME_FEATURE_DEFAULTS in src/lib/features.ts).
 */
export function listPlatformFeatureFlags(
    signal?: AbortSignal,
): Promise<ListPlatformFeatureFlagsResponse> {
    return apiFetch<ListPlatformFeatureFlagsResponse>(
        '/v1/citadel/feature-flags/',
        { signal },
    );
}

/**
 * PATCH /v1/citadel/feature-flags/{key}
 *
 * Upserts the override row and records a forensics-log entry server-side.
 */
export function setPlatformFeatureFlag(
    key: string,
    value: boolean,
    description?: string,
): Promise<PlatformFeatureFlag> {
    const body: { value: boolean; description?: string } = { value };
    if (description !== undefined) body.description = description;
    return apiFetch<PlatformFeatureFlag>(
        `/v1/citadel/feature-flags/${encodeURIComponent(key)}`,
        {
            method: 'PATCH',
            body: JSON.stringify(body),
        },
    );
}

// ── Template moderation (BL-29-1820 · BL-29-1828 · BL-29-1834 scaffold) ──

/**
 * Template domain type as returned by the Citadel admin endpoints.
 * Mirrors the Go store.Template struct JSON shape (citadel_templates.go).
 */
export interface CadminTemplate {
    id: string;
    tenant_id: string | null;
    workspace_id: string | null;
    deployment_target: string | null;
    name: string;
    description: string;
    category: string;
    icon: string;
    difficulty: string;
    composes: unknown;
    tags: string[];
    tier_minimum: string;
    status: 'draft' | 'published' | 'archived';
    origin: 'noeura' | 'user' | 'eidolon' | 'adhoc';
    created_by: string | null;
    rejection_reason: string | null;
    created_at: string;
    updated_at: string;
    source_table: 'platform' | 'tenant' | '';
}

export interface CadminTemplateListResponse {
    items: CadminTemplate[];
    next_cursor: string;
}

export interface CadminTemplateActionResponse {
    template_id: string;
    status: string;
    reason?: string;
}

/**
 * GET /v1/citadel/templates/moderation-queue
 *
 * Returns draft templates awaiting Citadel ratification, oldest-first.
 * Requires operations:read permission at the BE gate (perm("operations:read")).
 */
export function listModerationQueue(
    cursor?: string,
    signal?: AbortSignal,
): Promise<CadminTemplateListResponse> {
    const qs = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
    return apiFetch<CadminTemplateListResponse>(
        `/v1/citadel/templates/moderation-queue${qs}`,
        { signal },
    );
}

/**
 * POST /v1/citadel/templates/{id}/approve
 *
 * Transitions a draft template to published. Emits an OPAL audit receipt.
 * Requires operations:write permission at the BE gate.
 */
export function approveTemplate(id: string): Promise<CadminTemplateActionResponse> {
    return apiFetch<CadminTemplateActionResponse>(
        `/v1/citadel/templates/${encodeURIComponent(id)}/approve`,
        { method: 'POST' },
    );
}

/**
 * POST /v1/citadel/templates/{id}/reject
 *
 * Transitions a draft template to archived with a mandatory reason.
 * Emits an OPAL audit receipt. Requires operations:write permission.
 */
export function rejectTemplate(
    id: string,
    reason: string,
): Promise<CadminTemplateActionResponse> {
    return apiFetch<CadminTemplateActionResponse>(
        `/v1/citadel/templates/${encodeURIComponent(id)}/reject`,
        { method: 'POST', body: JSON.stringify({ reason }) },
    );
}

/**
 * GET /v1/citadel/templates/published
 *
 * Returns all published templates (platform + tenant union), newest-first.
 * Requires operations:read permission at the BE gate.
 */
export function listPublished(
    cursor?: string,
    signal?: AbortSignal,
): Promise<CadminTemplateListResponse> {
    const qs = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
    return apiFetch<CadminTemplateListResponse>(
        `/v1/citadel/templates/published${qs}`,
        { signal },
    );
}

// ── Trust integrity — run-status reconciliation (BL-29-2194 / BL-29-2170) ──

/**
 * One trust-integrity violation row returned by the reconciliation monitor.
 *
 * Field names match the Go `RunStatusDivergenceRow` JSON tags exactly
 * (workspace/operon/internal/database/interfaces.go):
 *   orbiset_run_id, tenant_id, workspace_id, deployment_target,
 *   orbiset_run_status, failed_orb_run_count, started_at, completed_at.
 */
export interface RunStatusDivergenceRow {
    /** Primary key of the offending orbiset_runs row. */
    orbiset_run_id: string;
    /** Composite-scope tenant. */
    tenant_id: string;
    /** Composite-scope workspace. */
    workspace_id: string;
    /** Composite-scope deployment target (live | sandbox). */
    deployment_target: string;
    /** Status claimed by the orbiset_runs row (SUCCEEDED or legacy SUCCESS). */
    orbiset_run_status: string;
    /** Number of child orb_runs rows in FAILED status for this run. */
    failed_orb_run_count: number;
    /** orbiset_run.started_at — ISO 8601 UTC. */
    started_at: string;
    /** orbiset_run.completed_at — ISO 8601 UTC, or null when not set. */
    completed_at: string | null;
}

/**
 * Response envelope from GET /v1/citadel/integrity/run-status-reconciliation.
 *
 * total_count is the true cross-table count (unbounded).
 * returned_count is len(divergences), ≤ limit.
 * limit is the applied cap (default and max 200).
 */
export interface RunStatusReconciliationResponse {
    total_count: number;
    returned_count: number;
    limit: number;
    divergences: RunStatusDivergenceRow[];
}

/**
 * GET /v1/citadel/integrity/run-status-reconciliation
 *
 * Returns every orbiset_runs row claiming SUCCEEDED while a child orb_runs
 * row is FAILED. A total_count of 0 means the platform is clean.
 * Requires perm("operations:read") — Citadel admin only (BL-29-1585).
 *
 * @param limit  Optional cap, 1–200. Defaults to 200 on the server.
 * @param signal AbortSignal for request cancellation.
 */
export function listRunStatusDivergences(
    limit?: number,
    signal?: AbortSignal,
): Promise<RunStatusReconciliationResponse> {
    const qs = limit !== undefined ? `?limit=${limit}` : '';
    return apiFetch<RunStatusReconciliationResponse>(
        `/v1/citadel/integrity/run-status-reconciliation${qs}`,
        { signal },
    );
}
